/**
 * AttachmentPreviewModal — full-screen overlay for previewing chat attachments.
 *
 * Supports: images (full-size preview), videos (inline player), PDFs (embedded viewer),
 * and generic files (icon + metadata with download button).
 *
 * Press Escape or click the backdrop to close.
 */
import React, { useEffect, useCallback } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import CloseButton from '../CloseButton/CloseButton';
import type { MessageAttachment } from '../../api/chat';
import { mediaSrc } from '../../api/mediaUrl';

interface AttachmentPreviewModalProps {
  attachment: MessageAttachment;
  onClose: () => void;
}

/** Format bytes into a human-readable size string. */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AttachmentPreviewModal({
  attachment,
  onClose,
}: AttachmentPreviewModalProps) {
  const { t } = useAppTranslation('chat');
  const url = mediaSrc(attachment.fileKey) ?? '';
  const ct = attachment.contentType;

  // Close on Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    // Prevent body scroll while modal is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prev;
    };
  }, [handleKeyDown]);

  // Close on backdrop click (not content click)
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = `${url}?download=true`;
    a.download = attachment.fileName;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const isImage = ct.startsWith('image/');
  const isVideo = ct.startsWith('video/');
  const isPdf = ct === 'application/pdf';

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Top bar: filename + close/download buttons */}
      <div
        style={{
          position: 'absolute',
          insetBlockStart: 0,
          insetInlineStart: 0,
          insetInlineEnd: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1,
        }}
      >
        <div style={{ color: '#fff', fontSize: 14, fontWeight: 500, minWidth: 0 }}>
          <span
            style={{
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {attachment.fileName}
          </span>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
            {formatFileSize(attachment.fileSizeBytes)}
          </span>
        </div>

        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button
            onClick={handleDownload}
            style={{
              padding: '6px 14px',
              fontSize: 13,
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: 6,
              color: '#fff',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
            }}
          >
            {t('attachments.download')}
          </button>
          <CloseButton onClick={onClose} size="lg" variant="overlay" />
        </div>
      </div>

      {/* Preview content */}
      <div
        style={{
          maxWidth: '90vw',
          maxHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isImage && (
          <img
            src={url}
            alt={attachment.fileName}
            style={{
              maxWidth: '90vw',
              maxHeight: '80vh',
              objectFit: 'contain',
              borderRadius: 4,
            }}
          />
        )}

        {isVideo && (
          <video
            src={url}
            controls
            autoPlay
            style={{
              maxWidth: '90vw',
              maxHeight: '80vh',
              borderRadius: 4,
            }}
          />
        )}

        {isPdf && (
          <iframe
            src={url}
            title={attachment.fileName}
            style={{
              width: '80vw',
              height: '80vh',
              border: 'none',
              borderRadius: 4,
              background: '#fff',
            }}
          />
        )}

        {!isImage && !isVideo && !isPdf && (
          <div
            style={{
              textAlign: 'center',
              color: '#fff',
              padding: 40,
            }}
          >
            <div style={{ fontSize: 64, marginBlockEnd: 16 }}>
              {ct.startsWith('audio/') ? '🎵' : '📎'}
            </div>
            <div style={{ fontSize: 18, fontWeight: 500, marginBlockEnd: 8 }}>
              {attachment.fileName}
            </div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBlockEnd: 20 }}>
              {formatFileSize(attachment.fileSizeBytes)} — {ct}
            </div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
              {t('attachments.noPreview')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
